import { DOCUMENT, inject, Injectable, RendererFactory2 } from '@angular/core';

import { Utils } from '@ideals/utils';

interface LogRocketInitOptions {
  browser?: {
    urlSanitizer?: (url: string) => string;
  };
  dom?: {
    inputSanitizer?: boolean;
    textSanitizer?: boolean;
  };
  network?: {
    requestSanitizer?: (request: Request) => Request;
    responseSanitizer?: (response: Response) => Response;
  };
  rootHostname?: string;
}

declare global {
  interface Window {
    LogRocket: {
      captureException: (error: Error) => void;
      init: (key: string, options: LogRocketInitOptions) => void;
    };
  }
}

@Injectable({ providedIn: 'root' })
export class LogRocketService {
  readonly #document = inject(DOCUMENT);
  readonly #renderer2 = inject(RendererFactory2).createRenderer(null, null);

  #appId?: string;

  captureException(error: Error): void {
    if (!this.#appId) {
      return;
    }

    window.LogRocket?.captureException(error);
  }

  initialize(appId?: string): void {
    if (!appId) {
      return;
    }

    this.#appId = appId;
    this.#createScript(appId);
  }

  #createScript(appId: string): void {
    const script = this.#renderer2.createElement('script') as HTMLScriptElement;
    const scriptSrc = 'https://cdn.lgrckt-in.com/LogRocket.min.js';

    this.#renderer2.setAttribute(script, 'async', '');
    this.#renderer2.setAttribute(script, 'src', scriptSrc);
    this.#renderer2.setAttribute(script, 'crossorigin', 'anonymous');
    this.#renderer2.appendChild(this.#document.head, script);

    script.onload = () => {
      window.LogRocket.init(appId, {
        network: {
          requestSanitizer: (request) => {
            return {
              ...request,
              body: null,
            };
          },
          responseSanitizer: (response) => {
            return {
              ...response,
              body: null,
            };
          },
        },
        rootHostname: `.${Utils.domain}`,
      });
    };

    this.#renderer2.appendChild(this.#document.head, script);
  }
}
